import L from 'leaflet';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import floorplanLatLngToPlan from '../../utils/plans/floorplanLatLngToPlan';

import useLeafletLayer from './useLeafletLayer';

const TILE_PX = 256;
// The drawing region behind one map tile is rendered no larger than this, whatever the zoom
const REGION_MAX_PX = 1024;
const NO_SHIFT = {
  x: 0,
  y: 0,
};

//
// The drawing laid over the geographic map through a calibration, as a GridLayer: each map
// tile works out the drawing region that covers it (the calibration is a similarity, so the
// mapping is affine over a tile), has it rendered by a PlanRenderer, and draws it rotated and
// scaled on its canvas, clipped to the crop's rectangle. Leaflet cannot rotate a tile layer,
// hence this layer. The calibration and page can change: the layer redraws on redraw(). The
// region is worked out in crop coordinates and rendered from the page at the page's offset.
//
const OverlayGridLayer = L.GridLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement('canvas');
    tile.width = TILE_PX;
    tile.height = TILE_PX;
    const {
      renderer,
      source,
    } = this.options;
    const {
      fit,
      page,
      rect,
      pageOffset,
      maxPxPerUnit,
    } = source.current;
    if (!fit) {
      done(null, tile);
      return tile;
    }

    const origin = coords.scaleBy(this.getTileSize());
    const toPlan = (dx, dy) => floorplanLatLngToPlan(fit, this._map.unproject(origin.add([dx, dy]), coords.z));
    const nw = toPlan(0, 0);
    const ne = toPlan(TILE_PX, 0);
    const sw = toPlan(0, TILE_PX);
    const se = toPlan(TILE_PX, TILE_PX);
    if (!(nw && ne && sw && se)) {
      done(null, tile);
      return tile;
    }

    // planX = a.px + c.py + e, planY = b.px + d.py + f, for tile pixels px, py
    const a = (ne.planX - nw.planX) / TILE_PX;
    const b = (ne.planY - nw.planY) / TILE_PX;
    const c = (sw.planX - nw.planX) / TILE_PX;
    const d = (sw.planY - nw.planY) / TILE_PX;
    const e = nw.planX;
    const f = nw.planY;
    const det = (a * d) - (b * c);
    if (det === 0) {
      done(null, tile);
      return tile;
    }

    const xs = [nw, ne, sw, se].map((point) => point.planX);
    const ys = [nw, ne, sw, se].map((point) => point.planY);
    const left = Math.max(rect.x, Math.min(...xs));
    const top = Math.max(rect.y, Math.min(...ys));
    const right = Math.min(rect.x + rect.width, Math.max(...xs));
    const bottom = Math.min(rect.y + rect.height, Math.max(...ys));
    if ((right <= left) || (bottom <= top)) {
      done(null, tile);
      return tile;
    }

    // Drawing pixels per unit: the tile's own density, within the page's and the region cap
    const unitsPerTilePx = Math.hypot(a, b);
    let scale = Math.min(1 / unitsPerTilePx, maxPxPerUnit);
    scale = Math.min(scale, REGION_MAX_PX / Math.max(right - left, bottom - top));
    const width = Math.max(1, Math.ceil((right - left) * scale));
    const height = Math.max(1, Math.ceil((bottom - top) * scale));

    const ticket = renderer.render({
      page,
      scale,
      left: (left + pageOffset.x) * scale,
      top: (top + pageOffset.y) * scale,
      width,
      height,
    });
    tile.pxTicket = ticket;
    ticket.promise.then(
      (bitmap) => {
        const context = tile.getContext('2d');
        // Canvas user space becomes drawing units: the inverse of the tile-to-drawing mapping
        context.setTransform(
          d / det,
          -b / det,
          -c / det,
          a / det,
          ((c * f) - (d * e)) / det,
          ((b * e) - (a * f)) / det,
        );
        context.beginPath();
        context.rect(rect.x, rect.y, rect.width, rect.height);
        context.clip();
        context.drawImage(bitmap, left, top, width / scale, height / scale);
        bitmap.close();
        done(null, tile);
      },
      (err) => {
        if (err.message !== 'abandoned') done(err, tile);
      },
    );
    return tile;
  },
});

function LeafletPlanOverlayLayer(props) {
  const {
    renderer,
    fit,
    page,
    rect,
    pageOffset = NO_SHIFT,
    maxPxPerUnit,
    opacity = 0.5,
    zIndex = 1,
    layerRef,
  } = props;
  // Read at tile creation, so a new calibration needs no new layer
  const source = useRef({});
  source.current = {
    fit,
    page,
    rect,
    pageOffset,
    maxPxPerUnit,
  };
  const optionsRef = useRef({
    renderer,
    opacity,
    zIndex,
  });

  const layer = useLeafletLayer(
    () => {
      const created = new OverlayGridLayer({
        renderer: optionsRef.current.renderer,
        source,
        opacity: optionsRef.current.opacity,
        zIndex: optionsRef.current.zIndex,
        tileSize: TILE_PX,
        updateWhenZooming: false,
        keepBuffer: 1,
      });
      created.on('tileunload', ({ tile }) => tile.pxTicket?.abandon());
      return created;
    },
  );

  useEffect(
    () => {
      layer?.redraw();
    },
    [layer, fit, page, rect, pageOffset.x, pageOffset.y],
  );

  useEffect(
    () => {
      layer?.setOpacity(opacity);
    },
    [layer, opacity],
  );

  useEffect(
    () => { layer?.setZIndex(zIndex); },
    [layer, zIndex],
  );

  useEffect(
    () => {
      if (layerRef) layerRef.current = layer;
    },
    [layer, layerRef],
  );

  return null;
}

LeafletPlanOverlayLayer.propTypes = {
  renderer: PropTypes.shape({ render: PropTypes.func.isRequired }).isRequired,
  // { origin, matrix } from floorplanCalibrationFit, or null while there is none
  fit: PropTypes.shape({
    origin: PropTypes.shape({}),
    matrix: PropTypes.arrayOf(PropTypes.number),
  }),
  page: PropTypes.number.isRequired,
  rect: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  // Where the page draws the crop's coordinates, in page units (cropPageOffset)
  pageOffset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  // Drawing pixels per page unit the source holds: a raster page's own resolution
  maxPxPerUnit: PropTypes.number.isRequired,
  opacity: PropTypes.number,
  // Stacking among the map's tile layers, Leaflet's 1 by default
  zIndex: PropTypes.number,
  // Receives the Leaflet layer, for a parent that moves its container while the user drags it
  layerRef: PropTypes.shape({ current: PropTypes.shape({}) }),
};

export default LeafletPlanOverlayLayer;
